import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Search as SearchIcon, LayoutGrid, GalleryHorizontal } from "lucide-react";
import SocialLinks from "@/components/SocialLinks";
import HScroll from "@/components/HScroll";

function ResultCard({ u, wrapClass }) {
  return (
    <div className={`paper-card p-5 relative hover:-translate-y-1 transition-transform ${wrapClass}`} data-testid={`search-result-${u.screen_name}`}>
      <Link to={`/u/${u.screen_name}`} className="absolute inset-0 z-0" aria-label={`View @${u.screen_name}`} data-testid={`search-result-link-${u.screen_name}`}/>
      <div className="relative z-[1] pointer-events-none">
        <p className="font-serif-display text-xl">@{u.screen_name}</p>
        {u.name && <p className="text-sm text-[#5C5C5C]">{u.name}</p>}
        <div className="text-xs text-[#8A8A8A] mt-2 space-y-0.5">
          {(u.city || u.state) && <p>{[u.city, u.state].filter(Boolean).join(", ")}</p>}
          {u.high_school && <p>HS: {Array.isArray(u.high_school) ? u.high_school.join(", ") : u.high_school}</p>}
          {u.college && <p>College: {Array.isArray(u.college) ? u.college.join(", ") : u.college}</p>}
        </div>
      </div>
      <SocialLinks socials={u.socials} size="sm" label="" className="relative z-[2] mt-3" testId={`search-socials-${u.screen_name}`}/>
    </div>
  );
}

export default function Search() {
  const [q, setQ] = useState("");
  const [mode, setMode] = useState("screen_name");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("mp_search_view") || "row"; } catch { return "row"; }
  });

  const setViewMode = (v) => {
    setView(v);
    try { localStorage.setItem("mp_search_view", v); } catch { /* ignore */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/search", { params: { q, mode } });
      setResults(data.results);
      setSearched(true);
    } finally { setLoading(false); }
  };

  return (
    <div className="paper-grain min-h-screen" data-testid="search-page">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <p className="font-script text-2xl text-[#8C3A3A]">find someone</p>
        <h1 className="font-serif-display text-4xl mb-2">Search MemoryPeaks</h1>
        <p className="text-[#5C5C5C] mb-8">By default only screen names are searchable. Switch to "all fields" to search across user-opted-in details (city, schools, etc.). Separate multiple terms with commas — e.g. <em>"astor, lakeview high school"</em> — and partial words match (lakeview finds Lakeview High School).</p>

        <form onSubmit={submit} className="paper-card p-6 mb-10">
          <div className="flex flex-col sm:flex-row gap-3">
            <input data-testid="search-query-input" className="mp-input flex-1" placeholder="screen name, city, school..." value={q} onChange={(e) => setQ(e.target.value)}/>
            <select data-testid="search-mode-select" className="mp-input sm:w-48" value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="screen_name">Screen name only</option>
              <option value="all">All fields</option>
            </select>
            <button data-testid="search-submit-btn" type="submit" disabled={loading} className="btn-primary">
              <SearchIcon className="w-4 h-4"/> {loading ? "..." : "Search"}
            </button>
          </div>
        </form>

        {searched && results.length === 0 && (
          <p className="text-center text-[#8A8A8A] italic py-12" data-testid="search-empty">No matches found.</p>
        )}

        {results.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-[#5C5C5C]">{results.length} {results.length === 1 ? "result" : "results"}</p>
              <div className="flex items-center gap-1 bg-[#F0EBE1] p-1 rounded-full" data-testid="search-view-toggle">
                <button
                  onClick={() => setViewMode("row")}
                  aria-label="Row view"
                  data-testid="search-view-row"
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${view === "row" ? "bg-white shadow text-[#8C3A3A]" : "text-[#8A8A8A] hover:text-[#5C5C5C]"}`}
                >
                  <GalleryHorizontal className="w-4 h-4"/>
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                  data-testid="search-view-grid"
                  className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${view === "grid" ? "bg-white shadow text-[#8C3A3A]" : "text-[#8A8A8A] hover:text-[#5C5C5C]"}`}
                >
                  <LayoutGrid className="w-4 h-4"/>
                </button>
              </div>
            </div>

            {view === "row" ? (
              <HScroll testId="search-results-carousel">
                {results.map((u) => (
                  <ResultCard key={u.id} u={u} wrapClass="w-64 shrink-0 snap-start"/>
                ))}
              </HScroll>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="search-results-grid">
                {results.map((u) => (
                  <ResultCard key={u.id} u={u} wrapClass=""/>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
